import { accessTokens } from 'api/accessTokenMgmt'

// Local signing shadow mode (app/services/local_signing_shadow.rb): fetch server-signed
// transactions to re-sign on this device, report the result and diagnostics.

export interface ShadowProbe {
    id: string
    kind: string
    server_txid: string
    sent_at: string
}

const base = () => `${window.knovApiUrl}/api/v1/local_signing_shadow`

function headers(): Record<string, string> {
    return {
        ...accessTokens(),
        'X-CSRF-TOKEN': gon.formToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
    }
}

async function request<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${base()}${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers: headers(),
        cache: 'no-store',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) throw new Error(`Shadow request ${path} failed (${response.status})`)
    if (response.status === 204) return undefined as T
    return response.json()
}

export const fetchPendingShadowProbes = () =>
    request<{ probes: ShadowProbe[] }>('/probes').then(result => result.probes || [])

export const fetchShadowProbe = (probeId: string) =>
    request<{ probe: ShadowProbe; beef: string }>(`/probes/${encodeURIComponent(probeId)}`)

export const reportShadowProbeResult = (
    probeId: string,
    result: {
        device_id: string | null
        /** Which probe kinds this client's re-signer understands (SHADOW_SIGNER_VERSION). */
        signer_version: number
        device_txid?: string
        sign_ms?: number
        fetch_ms?: number
        signed_inputs?: number
        foreign_inputs?: number
        visible?: boolean
        error?: string
    },
) => request<{ status: string }>(`/probes/${encodeURIComponent(probeId)}/result`, result)

export type ServerShareLookup =
    | { status: 'present'; treechatShare: string }
    | { status: 'missing' } // 404: Treechat holds no share for this device
    | { status: 'forbidden' } // 403: the account may not use device shares (not enrolled)
    | { status: 'unknown' } // network or server error: says nothing about the share

/** Treechat's share for this device, telling "none" apart from "couldn't ask". */
export async function lookUpServerShare(deviceId: string): Promise<ServerShareLookup> {
    try {
        const response = await fetch(
            `${gon.api.bsv}/server_share?device_id=${encodeURIComponent(deviceId)}`,
            { headers: headers(), cache: 'no-store' },
        )
        if (response.status === 404) return { status: 'missing' }
        if (response.status === 403) return { status: 'forbidden' }
        if (!response.ok) return { status: 'unknown' }
        const body = await response.json()
        return body?.treechat_share
            ? { status: 'present', treechatShare: body.treechat_share }
            : { status: 'unknown' }
    } catch (_error) {
        return { status: 'unknown' }
    }
}

/** This browser deleted its copy of revoked shadow keys. */
export const confirmShadowCleanup = (deviceId: string) =>
    request<void>('/cleanup', { device_id: deviceId })

export type ShadowEventKind =
    | 'key_check'
    | 'bootstrap'
    | 'bootstrap_failed'
    | 'probe_error'
    | 'probe_received'
    | 'probe_skipped'

export const reportShadowEvent = (
    kind: ShadowEventKind,
    deviceId: string | null,
    data: Record<string, string | number | boolean | null | undefined>,
) => request<void>('/events', { kind, device_id: deviceId, data })
